<template>
	<form class="product-mods-wrap" @submit.prevent>
		<template v-for="category in currentForcedModCategories">
			<div
				v-if="category.forcedMods.length"
				class="product-mods-holder"
				:class="{ error: category.isInvalid && isShowErrors }"
				:ref="category.id"
				:key="category.id"
			>
				<div class="product-mods-header">
					<div class="product-mods-header-holder">
						<span class="product-mods-title">
							{{ category.name }}
						</span>
					</div>
					<template v-if="category.numberMods > 0">
						<span class="d-flex flex-align-center">
							<span
								v-if="category.ruleMod === forcedModsRules['MUST_CHOOSE']"
								class="product-mods-header-note"
							>
								<span>Select {{ category.numberMods }}</span>
							</span>
							<span v-else class="product-mods-header-note">
								Select up to {{ category.numberMods }}
							</span>
							<span class="product-mods-header-note--error">Required</span>
						</span>
					</template>
				</div>

				<ul v-if="category.numberMods === 1" class="product-mods-list">
					<template v-for="forcedMod in category.forcedMods">
						<li
							v-if="forcedMod.name"
							:key="forcedMod.id"
							:class="{ hidden: forcedMod.isOptionHidden }"
						>
							<custom-radio-button
								:id="forcedMod.id"
								:class="{
									inversion: forcedMod.id.includes('can-choose')
								}"
								:value="forcedMod.id"
								:checked-value="getRadioCheckedValue(category)"
								:name="category.id"
								:disabled="checkIsModDisabled(category, forcedMod)"
								type="rectangle"
								@change="changeForcedMod(category, forcedMod)"
							>
								<label
									class="radio-label"
									:class="{ disabled: checkIsModDisabled(category, forcedMod) }"
									:for="forcedMod.id"
								>
									{{ forcedMod.name }}
									<span v-if="forcedMod.price" class="product-mods-price">
										+<formatted-price
											:price="forcedMod.price"
											decimal-height="70"
										/>
									</span>
								</label>
							</custom-radio-button>
						</li>
					</template>
				</ul>

				<ul v-else class="product-mods-list">
					<template v-for="forcedMod in category.forcedMods">
						<li
							v-if="forcedMod.name"
							:key="forcedMod.id"
							:class="{ hidden: forcedMod.isOptionHidden }"
						>
							<custom-check-box
								:id="forcedMod.id"
								:class="{
									inversion: forcedMod.id.includes('can-choose')
								}"
								:checked="forcedMod.isSelected"
								:disabled="checkIsModDisabled(category, forcedMod)"
								type="rectangle"
								@change="changeForcedMod(category, forcedMod)"
							>
								<label
									class="radio-label"
									:class="{ disabled: checkIsModDisabled(category, forcedMod) }"
									:for="forcedMod.id"
								>
									{{ forcedMod.name }}
									<span v-if="forcedMod.price" class="product-mods-price">
										+<formatted-price
											:price="forcedMod.price"
											decimal-height="70"
										/>
									</span>
								</label>
							</custom-check-box>
						</li>
					</template>
				</ul>
			</div>
		</template>

		<div
			v-if="acceptOnlineOrder && showItemRequest"
			class="product-mods-holder"
		>
			<div class="product-mods-header">
				<div class="product-mods-header-holder">
					<span class="product-mods-title">
						Special instructions
					</span>
				</div>
			</div>
			<textarea
				:value="productNotice"
				rows="1"
				maxlength="300"
				placeholder="Enter description"
				@input="changeProductNotice"
			/>
		</div>
	</form>
</template>

<script>
import { mapState } from 'vuex'
import currency from 'currency.js'

import CustomRadioButton from '@/components/forms/CustomRadioButton'
import CustomCheckBox from '@/components/forms/CustomCheckBox'

import { FORCED_MODS_RULES } from '@/constants'
import FormattedPrice from '@/components/FormattedPrice.vue'

export default {
	name: 'EditProductMods',

	components: {
		FormattedPrice,
		CustomRadioButton,
		CustomCheckBox
	},

	props: {
		forcedModCategories: {
			type: Array,
			default: () => []
		},
		productInCart: {
			type: Object,
			default: () => {}
		},
		productNotice: {
			type: String,
			default: ''
		},
		validate: {
			type: Number,
			default: 0
		},
		showErrors: {
			type: Boolean,
			default: false
		},
		acceptOnlineOrder: {
			type: Boolean,
			default: false
		},
		acceptItemRequest: {
			type: [Boolean, null]
		}
	},

	data() {
		return {
			currentForcedModCategories: {},
			isShowErrors: false
		}
	},

	created() {
		this.setCurrentForcedModCategories()
	},

	computed: {
		...mapState('cart', ['cartItems']),
		...mapState('venue', ['venueInfo']),

		forcedModsRules() {
			return FORCED_MODS_RULES
		},

		acceptProductNote() {
			const { settings } = this.venueInfo
			if (settings) {
				const { acceptProductNote: productNote } = settings
				return productNote === true || productNote === false
					? productNote
					: true
			}
			return false
		},

		showItemRequest() {
			const acceptItemRequest = this.acceptItemRequest
			return acceptItemRequest === true || acceptItemRequest === false
				? this.acceptProductNote && acceptItemRequest
				: true // enable if acceptItemRequest setting not presented in venue info (venue chain <3.5)
		}
	},

	watch: {
		forcedModCategories: {
			handler: function() {
				this.setCurrentForcedModCategories()
			},
			deep: true
		},

		validate() {
			this.validation()
		},

		showErrors(val) {
			this.isShowErrors = val
		}
	},

	methods: {
		setCurrentForcedModCategories() {
			const productInCart = this.productInCart

			this.currentForcedModCategories = this.forcedModCategories.map(
				category => {
					const forcedMods = category.forcedMods.reduce((acc, item) => {
						if (item.isVisible) {
							acc = [
								...acc,
								{
									id: item.id,
									price: item.customPrice,
									name: item.modifier && item.modifier.name,
									isSelected: !!(
										productInCart &&
										productInCart.modifiers.find(
											modifier => modifier.id === item.id
										)
									)
								}
							]
							return acc
						}
						return acc
					}, [])

					let newCategory = {
						id: category.id,
						name: category.modifierCategory.name,
						ruleMod: category.ruleMod,
						typeMod: category.typeMod,
						numberMods: category.numberMods,
						canChooseNone: category.canChooseNone,
						isInvalid: false,
						forcedMods
					}

					if (category.canChooseNone && forcedMods.length) {
						const modifiersIds = productInCart.modifiers.map(mod => mod.id)
						const isCanChooseNoneSelected = !category.forcedMods.some(mod =>
							modifiersIds.includes(mod.id)
						)

						newCategory.forcedMods = [
							...newCategory.forcedMods,
							{
								id: `${category.id}-can-choose`,
								name: 'NONE OF THESE',
								isSelected: !isCanChooseNoneSelected,
								isOptionHidden: !isCanChooseNoneSelected
							},
							{
								id: `${category.id}-can-not-choose`,
								name: 'NONE OF THESE',
								isSelected: isCanChooseNoneSelected,
								isOptionHidden: isCanChooseNoneSelected
							}
						]
					}

					return newCategory
				}
			)
		},

		changeForcedMod(category, forcedMod) {
			const isCanChooseNone = forcedMod.id.includes('can-not-choose')
			const isCanChoose = forcedMod.id.includes('can-choose')

			if (isCanChooseNone || isCanChoose) {
				this.toggleCanChooseNone(category, forcedMod)
			} else if (category.numberMods === 1) {
				this.toggleSelectedRadios(category, forcedMod)
			} else {
				this.toggleSelectedCheckboxes(forcedMod)
			}

			this.validateForcedModsCategory(category)

			this.$emit('change', this.getAllSelectedForcedMods())
			this.$forceUpdate()
		},

		toggleSelectedCheckboxes(forcedMod) {
			forcedMod.isSelected = !forcedMod.isSelected
		},

		toggleSelectedRadios(category, forcedMod) {
			const selectedForcedMod = category.forcedMods.find(mod => mod.isSelected)

			if (selectedForcedMod) {
				selectedForcedMod.isSelected = false
			}

			forcedMod.isSelected = true
		},

		toggleCanChooseNone(category, forcedMod) {
			const { forcedMods } = category
			const canChooseNoneMod = forcedMods.find(mod =>
				mod.id.includes('can-not-choose')
			)
			const canChooseMod = forcedMods.find(mod => mod.id.includes('can-choose'))

			forcedMod.isSelected = !forcedMod.isSelected

			if (forcedMod.isSelected) {
				category.forcedMods.forEach(mod => {
					mod.isSelected = mod.id === forcedMod.id
				})
			}

			const isCanChooseNoneModSelected =
				canChooseNoneMod && canChooseNoneMod.isSelected

			canChooseNoneMod.isOptionHidden = isCanChooseNoneModSelected
			canChooseMod.isOptionHidden = !isCanChooseNoneModSelected
		},

		checkIsModDisabled(category, forcedMod) {
			const isCanChoose = forcedMod.id.includes('can-choose')
			const isCanNoneChoose = forcedMod.id.includes('can-not-choose')

			if (isCanChoose || isCanNoneChoose) {
				return false
			}

			const { forcedMods, numberMods } = category
			const canChooseNoneMod = forcedMods.find(mod =>
				mod.id.includes('can-not-choose')
			)

			if (canChooseNoneMod && canChooseNoneMod.isSelected) {
				return true
			} else if (numberMods > 1) {
				const selectedIds = this.getCategorySelectedForcedMods(category)
				const isMaxLength = selectedIds.length >= category.numberMods

				return isMaxLength && !forcedMod.isSelected
			}

			return false
		},

		getCategorySelectedForcedMods(category) {
			return category.forcedMods.filter(mod => {
				const isCanChooseNone = mod.id.includes('can-not-choose')
				const isCanChoose = mod.id.includes('can-choose')

				return mod.isSelected && !isCanChooseNone && !isCanChoose
			})
		},

		getAllSelectedForcedMods() {
			let allSelectedMods = []

			this.currentForcedModCategories.forEach(category => {
				const selectedMods = this.getCategorySelectedForcedMods(category)

				allSelectedMods = [...allSelectedMods, ...selectedMods]
			})

			return allSelectedMods
		},

		getFormattedPrice(price) {
			return currency(price)
		},

		getRadioCheckedValue(category) {
			const selectedForcedMod = category.forcedMods.find(forcedMod => {
				return forcedMod.isSelected
			})

			return selectedForcedMod ? selectedForcedMod.id : ''
		},

		changeProductNotice(e) {
			this.$emit('change-product-notice', e.target.value.trim())
		},

		validateMustChoose(category, numberMods) {
			const canChooseNonMod = category.forcedMods.find(mod =>
				mod.id.includes('can-not-choose')
			)
			const isCanChooseModSelected =
				canChooseNonMod && canChooseNonMod.isSelected
			const categoryIndex = this.currentForcedModCategories.findIndex(
				currentCategory => currentCategory.id === category.id
			)
			const selectedIds = this.getCategorySelectedForcedMods(category)

			const newCategory = {
				...category,
				isInvalid: selectedIds.length < numberMods && !isCanChooseModSelected
			}

			this.$set(this.currentForcedModCategories, categoryIndex, newCategory)
		},

		validateForcedModsCategory(category) {
			if (category.ruleMod === this.forcedModsRules['MUST_CHOOSE']) {
				this.validateMustChoose(category, category.numberMods)
			} else if (
				category.ruleMod === this.forcedModsRules['UP_TO'] &&
				category.numberMods > 0
			) {
				this.validateMustChoose(category, 1)
			}
		},

		validation() {
			this.currentForcedModCategories.forEach(category =>
				this.validateForcedModsCategory(category)
			)

			const invalidForcedModsCategoryIds = this.currentForcedModCategories
				.filter(category => category.isInvalid)
				.map(category => category.id)

			this.$emit('set-is-valid', invalidForcedModsCategoryIds)
		}
	}
}
</script>
